import {
  BadgeCheck,
  BellRing,
  Cloud,
  CreditCard,
  EyeOff,
  FileQuestion,
  Film,
  ImageIcon,
  Link2,
  Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClientVaultEventSelector } from "../../components/ClientVaultEventSelector";
import { PageHeader } from "../../components/PageHeader";
import { IntelligenceWidgetGrid } from "../../components/dashboard/IntelligenceWidgetGrid";
import { getActivity, getClientDashboardIntelligence, getDashboardStats, getStorageBilling } from "../../lib/api";
import { formatBytes, formatDate } from "../../lib/format";
import { useClientVaultContext, vaultQueryParams } from "../../lib/useClientVaultContext";
import type { ActivityItem, BusinessCenterIntelligencePayload, DashboardStats } from "../../lib/types";

const statCards = [
  { key: "pendingUploads", label: "Pending Uploads", icon: BellRing },
  { key: "approvedMedia", label: "Approved Media", icon: BadgeCheck },
  { key: "featuredMemories", label: "Featured on Memory Hub", icon: Sparkles },
  { key: "hiddenMedia", label: "Hidden / Private Media", icon: EyeOff },
  { key: "notApproved", label: "Not Approved", icon: FileQuestion },
  { key: "totalPhotos", label: "Total Photos", icon: ImageIcon },
  { key: "totalVideos", label: "Total Videos", icon: Film },
];

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [billingUrl, setBillingUrl] = useState("");
  const [billingMessage, setBillingMessage] = useState("");
  const [intelligence, setIntelligence] = useState<BusinessCenterIntelligencePayload | null>(null);
  const vaultContext = useClientVaultContext();

  useEffect(() => {
    void getActivity().then(setActivity);
    void getStorageBilling().then((billing) => {
      setBillingUrl(billing.billingUrl || "");
      setBillingMessage(billing.fallbackMessage);
    });
  }, []);

  useEffect(() => {
    if (vaultContext.loading) return;
    if (!vaultContext.context || !vaultContext.selectedVault) {
      setStats(null);
      setIntelligence(null);
      return;
    }
    const params = vaultQueryParams(vaultContext.context, vaultContext.selectedVault);
    void getDashboardStats(params).then(setStats);
    void getClientDashboardIntelligence({ clientId: vaultContext.context.client.id })
      .then(setIntelligence)
      .catch(() => setIntelligence(null));
  }, [vaultContext.loading, vaultContext.selectedVaultId, vaultContext.context?.client.id]);

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Memory Media Vault Dashboard"
        title="Welcome to your Memory Media Vault."
        subtitle="Review guest uploads, approved galleries, featured memories, and event vault activity from your KreatetheDate client workspace."
        >
        <div className="vault-rail rounded-lg border border-ktd-gold/25 bg-white/[0.06] p-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-ktd-softGold">Upload Link Status</p>
          <p className="mt-3 text-3xl font-black capitalize text-white">{stats?.uploadLinkStatus || "open"}</p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Link
              to="/client/memory-hub/vault"
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-4 py-2 text-sm font-extrabold text-white"
            >
              <Cloud className="size-4" />
              Open Vault
            </Link>
            <Link
              to="/client/upload-link"
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-ktd-gold px-4 py-2 text-sm font-extrabold text-ktd-black"
            >
              <Link2 className="size-4" />
              Copy Guest Link
            </Link>
          </div>
        </div>
      </PageHeader>

      <ClientVaultEventSelector
        context={vaultContext.context}
        selectedVault={vaultContext.selectedVault}
        selectedVaultId={vaultContext.selectedVaultId}
        loading={vaultContext.loading}
        error={vaultContext.error}
        onChange={vaultContext.selectVault}
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          const value = stats?.[card.key as keyof DashboardStats] ?? 0;
          return (
            <article key={card.key} className="vault-stat-card ivory-card rounded-lg p-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-bold text-ktd-muted">{card.label}</p>
                  <strong className="mt-2 block font-display text-4xl text-ktd-rich">{String(value)}</strong>
                </div>
                <span className="grid size-12 place-items-center rounded-lg bg-ktd-black text-ktd-softGold">
                  <Icon className="size-5" />
                </span>
              </div>
            </article>
          );
        })}
        <article className="vault-stat-card ivory-card rounded-lg p-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-bold text-ktd-muted">Storage Ownership Reminder</p>
              <strong className="mt-2 block font-display text-3xl text-ktd-rich">{formatBytes(stats?.storageBytes || 0)}</strong>
            </div>
            <span className="grid size-12 place-items-center rounded-lg bg-ktd-black text-ktd-softGold">
              <Cloud className="size-5" />
            </span>
          </div>
        </article>
      </section>

      {intelligence ? <IntelligenceWidgetGrid payload={intelligence} mode="client" /> : null}

      <section className="grid gap-6 lg:grid-cols-[1.15fr_.85fr]">
        <article className="vault-masthead overflow-hidden rounded-lg text-white shadow-luxe">
          <div className="p-6 sm:p-8">
            <p className="text-xs font-extrabold uppercase tracking-[0.24em] text-ktd-softGold">
              Storage Ownership & Billing
            </p>
            <h2 className="mt-4 font-display text-4xl font-bold">Keep your vault connected.</h2>
            <p className="mt-4 max-w-3xl leading-7 text-white/68">
              Your Memory Media Vault is connected to your client-owned Cloudflare account. To keep your photos, videos,
              guest uploads, and approved galleries active, please keep your cloud storage billing and payment method
              current.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              {billingUrl ? (
                <a
                  href={billingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-ktd-gold px-5 py-3 text-sm font-extrabold text-ktd-black"
                >
                  <CreditCard className="size-4" />
                  Manage Cloud Storage Billing
                </a>
              ) : (
                <span className="rounded-lg border border-ktd-gold/25 bg-white/[0.06] px-5 py-3 text-sm font-semibold leading-6 text-white/68">
                  {billingMessage}
                </span>
              )}
              <Link
                to="/client/storage-billing"
                className="focus-ring inline-flex justify-center rounded-lg border border-ktd-gold/35 px-5 py-3 text-sm font-extrabold text-ktd-softGold"
              >
                View Storage Instructions
              </Link>
              <Link
                to="/client/settings"
                className="focus-ring inline-flex justify-center rounded-lg border border-white/14 px-5 py-3 text-sm font-extrabold text-white"
              >
                Update Payment Reminder
              </Link>
            </div>
            <p className="mt-5 text-xs leading-6 text-white/52">
              Cloudflare billing is managed directly through your client-owned Cloudflare account. Kreatethedate does not
              control your Cloudflare payment method, invoices, billing access, or third-party storage charges.
            </p>
          </div>
        </article>

        <article className="vault-rail ivory-card rounded-lg p-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#D94A16]">Recent Activity</p>
          <div className="mt-5 grid gap-3">
            {activity.map((item) => (
              <div key={item.id} className="rounded-lg border border-ktd-gold/10 bg-white p-4">
                <p className="text-sm font-extrabold text-ktd-rich">{item.details}</p>
                <p className="mt-1 text-xs font-semibold text-ktd-muted">
                  {item.actorEmail || "System"} - {formatDate(item.createdAt)}
                </p>
              </div>
            ))}
          </div>
        </article>
      </section>
    </div>
  );
}
